import React, { useState, useRef, useEffect } from 'react';
import { Minus, Square, X } from 'lucide-react';
import { useWindows } from '../contexts/WindowContext';

interface WindowProps {
  id: string;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isMinimized: boolean;
  isMaximized: boolean;
  isActive: boolean;
  content: React.ReactNode;
}

const Window: React.FC<WindowProps> = ({
  id,
  title,
  x,
  y,
  width,
  height,
  isMinimized,
  isMaximized,
  isActive,
  content,
}) => {
  const { closeWindow, minimizeWindow, maximizeWindow, focusWindow, updateWindow } = useWindows();
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const windowRef = useRef<HTMLDivElement>(null);

  // Detect if user is on mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                   window.innerWidth <= 768;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('window-title')) {
      focusWindow(id);
      if (!isMobile) { // Only allow dragging on desktop
        setIsDragging(true);
        setDragStart({
          x: e.clientX - x,
          y: e.clientY - y,
        });
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('window-title')) {
      focusWindow(id);
      if (!isMobile) { // Only allow dragging on desktop (mobile windows are full-screen)
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - x,
          y: touch.clientY - y,
        });
      }
    }
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width,
      height,
    });
  };

  const handleMinimize = () => {
    setIsMinimizing(true);
    // Start the minimize animation, then actually minimize after animation completes
    setTimeout(() => {
      minimizeWindow(id);
      setIsMinimizing(false);
    }, 300); // Animation duration
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && !isMaximized && !isMobile) {
        const newX = e.clientX - dragStart.x;
        const newY = Math.max(0, e.clientY - dragStart.y);
        updateWindow(id, { x: newX, y: newY });
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        const newWidth = Math.max(300, resizeStart.width + deltaX);
        const newHeight = Math.max(200, resizeStart.height + deltaY);
        updateWindow(id, { width: newWidth, height: newHeight });
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && !isMaximized && !isMobile) {
        const touch = e.touches[0];
        const newX = touch.clientX - dragStart.x;
        const newY = Math.max(0, touch.clientY - dragStart.y);
        updateWindow(id, { x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, isResizing, dragStart, resizeStart, id, updateWindow, isMaximized, isMobile]);

  // Mobile-responsive window styling
  const windowStyle = isMobile
    ? { left: 0, top: 0, width: '100%' } // Full width on mobile, height auto
    : isMaximized
    ? { left: 0, top: 0, width: '100%', height: 'calc(100% - 30px)' }
    : { left: x, top: y, width, height };

  // Handle minimize animation and final hiding
  let finalStyle: React.CSSProperties = windowStyle;
  let windowClass = `window ${isActive ? 'active' : ''}`;
  
  if (isMinimizing) {
    // During minimize animation: scale down and move toward taskbar
    finalStyle = {
      ...windowStyle,
      transform: 'scale(0.1) translateY(50vh)',
      opacity: 0,
      transition: 'all 0.3s ease-in-out',
    };
    windowClass += ' minimizing';
  } else if (isMinimized) {
    // After animation: completely hide
    finalStyle = { ...windowStyle, display: 'none' };
  } else {
    // Normal state: no transition for smooth dragging
    finalStyle = windowStyle;
  }

  return (
    <div
      ref={windowRef}
      className={windowClass}
      style={finalStyle}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="window-titlebar">
        <div className="window-title">{title}</div>
        <div className="window-controls">
          <button
            className="window-control minimize"
            onClick={handleMinimize}
          >
            <Minus size={12} />
          </button>
          <button
            className="window-control maximize"
            onClick={() => maximizeWindow(id)}
          >
            <Square size={12} />
          </button>
          <button
            className="window-control close"
            onClick={() => closeWindow(id)}
          >
            <X size={12} />
          </button>
        </div>
      </div>
      <div className="window-content">
        {content}
      </div>
      {!isMaximized && !isMobile && (
        <div
          className="window-resize-handle"
          onMouseDown={handleResizeStart}
        />
      )}
    </div>
  );
};

export default Window;